import { useEffect, useState, type ReactNode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { overlayRoot } from '../../lib/overlay'

/**
 * The one dropdown. Every ▾ in the product opens this: filters, sorts, bulk
 * actions, the pickers inside a wizard, rename, a row's overflow. There is
 * never more than one open, and opening a second closes the first — which is
 * enforced here rather than asked of every call site.
 *
 * It is positioned against the **app frame**, not the viewport: the frame is
 * what an overlay belongs to, and measuring against the viewport would put a
 * menu over the header the moment the body was scrolled.
 */

export interface MenuItem {
  /** What comes back to onPick. Also what `current` is compared against. */
  value: string
  label: string
  /** Right-aligned, 11px muted — a count, a shortcut, a consequence. */
  note?: string
  /**
   * A second line under the label, which makes the row 44px instead of 34px.
   * The account menu's sign-out is the one item in the product that has one,
   * and it is a requirement rather than decoration: a member switched into
   * somebody else's account cannot otherwise tell what "Sign out" means. See
   * round two's `1a`.
   */
  detail?: string
  /** A 13px outline glyph before the label. */
  glyph?: ReactNode
  /** Drawn above this item — the account menu's rule before sign-out. */
  ruleBefore?: boolean
  /**
   * This one destroys something. Failure ink on the label, matching the danger
   * Action and StatusChip — a menu had no way to say it before, so Delete read
   * exactly like Archive next to it.
   */
  danger?: boolean
}

/**
 * The identity block some menus open with: who the menu is about, before what
 * it can do. Only the account menu has one so far.
 */
export interface MenuHeader {
  initials: string
  name: string
  /** The address, in `text-data` — what makes the menu's actions unambiguous. */
  detail: string
  /** True for an account being borrowed: a different gradient and a chip. */
  borrowed?: boolean
  chip?: string
}

interface MenuSpec {
  /** The optional 34px title row. */
  title?: string
  header?: MenuHeader
  items: MenuItem[]
  /** The item to draw as chosen. */
  current?: string | null
  onPick: (value: string) => void
}

const ITEM_H = 34
const TALL_ITEM_H = 44

let root: Root | null = null
let container: HTMLElement | null = null

function teardown() {
  root?.unmount()
  container?.remove()
  root = null
  container = null
}

/**
 * Open the menu under `trigger`. Call it from the trigger's own click handler
 * and hand it the element — `event.currentTarget` is exactly right.
 */
export function openAnchoredMenu(trigger: HTMLElement, spec: MenuSpec) {
  const host = overlayRoot()
  if (!host) return

  teardown()
  container = document.createElement('div')
  host.appendChild(container)
  root = createRoot(container)
  root.render(<AnchoredMenu trigger={trigger} spec={spec} onClose={teardown} />)
}

export function closeAnchoredMenu() {
  teardown()
}

function place(trigger: HTMLElement, spec: MenuSpec) {
  const host = overlayRoot()
  const frame = host?.getBoundingClientRect() ?? new DOMRect(0, 0, 1440, 900)
  const at = trigger.getBoundingClientRect()

  // 236 is the width the geometry is reasoned about at, even though the panel
  // itself is min 212 / max 290 — it is the number that decides whether there
  // is room, and using the rendered width instead would let a wide menu open
  // hard against the frame's edge.
  const w = 236
  const h =
    (spec.title ? ITEM_H : 10) +
    (spec.header ? 54 : 0) +
    spec.items.reduce((sum, item) => sum + (item.detail ? TALL_ITEM_H : ITEM_H) + (item.ruleBefore ? 11 : 0), 0) +
    10

  let x = at.left - frame.left
  if (x + w > frame.width - 14) x = Math.max(14, frame.width - w - 14)

  // Below the trigger by default; flipped above when it would run off the
  // bottom, which is what makes a menu on the last row of a table usable.
  let y = at.bottom - frame.top + 6
  if (y + h > frame.height - 14) y = Math.max(14, at.top - frame.top - h - 6)

  return { x, y }
}

function AnchoredMenu({
  trigger,
  spec,
  onClose,
}: {
  trigger: HTMLElement
  spec: MenuSpec
  onClose: () => void
}) {
  const [at] = useState(() => place(trigger, spec))
  const [hovered, setHovered] = useState<string | null>(null)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [onClose])

  return (
    <>
      {/* Invisible, but it still closes on click and still covers the page, so
          the next click anywhere dismisses rather than doing two things. */}
      <div
        className="tab"
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, zIndex: 44, cursor: 'default' }}
      />
      <div
        className="fr"
        role="menu"
        aria-label={spec.title}
        style={{
          position: 'absolute',
          left: at.x,
          top: at.y,
          minWidth: 212,
          maxWidth: 290,
          padding: 5,
          borderRadius: 12,
          border: '1px solid var(--w-line-strong)',
          background: 'var(--w-sheet)',
          boxShadow: 'var(--w-float)',
          zIndex: 45,
        }}
      >
        {spec.header && <IdentityBlock header={spec.header} />}
        {spec.title && (
          <div
            style={{
              padding: '7px 10px 6px',
              font: '600 11px Manrope,sans-serif',
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--color-muted)',
            }}
          >
            {spec.title}
          </div>
        )}
        {spec.items.map((item) => {
          const chosen = item.value === spec.current
          return (
            <ItemRule key={`${item.value}-rule`} before={item.ruleBefore}>
            <button
              key={item.value}
              type="button"
              role="menuitem"
              className="tab"
              onMouseEnter={() => setHovered(item.value)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => {
                onClose()
                spec.onPick(item.value)
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                minHeight: item.detail ? TALL_ITEM_H : ITEM_H,
                padding: item.detail ? '6px 9px' : '0 9px',
                borderRadius: 8,
                border: 0,
                cursor: 'pointer',
                textAlign: 'left',
                background: chosen || hovered === item.value ? 'var(--w-fill)' : 'transparent',
              }}
            >
              {item.glyph && (
                <span style={{ flex: 'none', display: 'flex', color: chosen ? 'var(--color-action-ink)' : 'var(--color-muted)' }}>
                  {item.glyph}
                </span>
              )}
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span
                  style={{
                    font: chosen ? '600 12px Manrope,sans-serif' : '500 12px Manrope,sans-serif',
                    color: item.danger
                      ? 'var(--w-danger-ink)'
                      : chosen
                        ? 'var(--color-amber-ink)'
                        : 'var(--color-text)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.label}
                </span>
                {item.detail && (
                  <span
                    className="text-data"
                    style={{
                      fontSize: 10,
                      color: 'var(--color-faint)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.detail}
                  </span>
                )}
              </span>
              {item.note && (
                <span
                  style={{
                    font: '500 11px Manrope,sans-serif',
                    color: 'var(--color-muted)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.note}
                </span>
              )}
            </button>
            </ItemRule>
          )
        })}
      </div>
    </>
  )
}

/**
 * Who the menu is about, before what it can do. The address is the working
 * part: it is what makes "Sign out" unambiguous for somebody holding two
 * accounts.
 */
function IdentityBlock({ header }: { header: MenuHeader }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 12px',
        margin: '-5px -5px 0',
        borderBottom: '1px solid var(--w-line)',
      }}
    >
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          font: '600 11px Manrope,sans-serif',
          color: '#f3e9e6',
          background: header.borrowed
            ? 'linear-gradient(150deg,#7a4a55,#2c1a20)'
            : 'linear-gradient(150deg,#a8602f,#3d212b)',
        }}
      >
        {header.initials}
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <span
            style={{
              font: '600 12px Manrope,sans-serif',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {header.name}
          </span>
          {header.chip && (
            <span
              style={{
                flex: 'none',
                padding: '2px 6px',
                borderRadius: 99,
                background: 'var(--w-pill-fill)',
                border: '1px solid var(--w-pill-line)',
                font: '600 9px Manrope,sans-serif',
                letterSpacing: '.08em',
                color: 'var(--w-pill-ink)',
              }}
            >
              {header.chip}
            </span>
          )}
        </span>
        <span
          className="text-data"
          style={{
            fontSize: 10,
            color: 'var(--color-faint)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {header.detail}
        </span>
      </span>
    </div>
  )
}

/** A hairline above an item, where a menu groups. */
function ItemRule({ before, children }: { before?: boolean; children: ReactNode }) {
  if (!before) return <>{children}</>

  return (
    <>
      <div style={{ height: 1, margin: '5px -5px', background: 'var(--w-line)' }} />
      {children}
    </>
  )
}

/**
 * The trigger itself — `{value} ▾` in 500 12px muted, which is how every
 * picker in the redesign presents itself when it isn't a segmented control.
 */
export function MenuTrigger({
  label,
  menu,
  style,
}: {
  label: string
  menu: MenuSpec | (() => MenuSpec)
  style?: React.CSSProperties
}) {
  return (
    <button
      type="button"
      className="tab"
      aria-haspopup="menu"
      onClick={(event) => openAnchoredMenu(event.currentTarget, typeof menu === 'function' ? menu() : menu)}
      style={{
        padding: 0,
        border: 0,
        background: 'none',
        font: '500 12px Manrope,sans-serif',
        color: 'var(--color-muted)',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        flex: 'none',
        ...style,
      }}
    >
      {label} ▾
    </button>
  )
}
